import Analytic from './Analytic.js';

// eslint
/* global cast, ATInternet */

export default class ATInternetDevelopers extends Analytic {
    /**
     * Create ATInternet Developers Analytics
     * @param {Object} options
     * @param {Logger} logger
     */
    constructor(options, logger, collectDomain, collectDomainSSL) {
        super(options, logger);
        this.name = 'AT Internet Developers';
        if (collectDomain && collectDomainSSL) {
            this.streamingAnalytics = new ATInternet.Tracker.Tag({ collectDomain: collectDomain, collectDomainSSL: collectDomainSSL, pixelPath:'/hit.xiti' });
        } else {
            this.streamingAnalytics = new ATInternet.Tracker.Tag();
        }
        this.media = {};
        this.mediaInformation = {};
        this.customData = {};
        this.mediaStatus = {
            PLAYING: 'PLAYING',
            PAUSED: 'PAUSED'
        };
        this.broadcastTypes = {
            LIVE: 'live',
            VOD: 'clip'
        };
        this.mediaTypes = {
            VIDEO: 'video'
        };
        this.playerTypes = {
            VIDEO: 1
        };
        this.pageTypes = {
            NAME: 'player',
            LEVEL2: 7
        };
        this.trackConstants = {
            MEDIA_TYPE: 'vid',
            PLATFORM: 'ott',
            APP_NAME: 'pl',
            CONTENT_TYPE: 'index',
            LANGUAGE: 'en',
            TITLE: 'Discover Standard | RTÉ Player',
            MEDIA_PLAYER: 'rte_smarttv_player',
            PLAYER_VERSION: '1.0'
        };
        this.resetMedia();
    }

    resetMedia() {
        this.media = {
            contentId: '',
            title: '',
            duration: 0,
            channel: '',
            genre: '',
            playlistProgName: '',
            episodeName: '',
            episodeNumber: '',
            seriesNumber: '',
            subGenre: '',
            pubDate: '',
            producer: ''
        };
    }

    startMediaSession() {
        this.stopMediaSession();
        this.mediaInformation = this.playerManager.getMediaInformation();
        this.customData = (this.mediaInformation && this.mediaInformation.customData) || {};
        this.resetMedia();
        this.parseCustomData();

        this.pageSet();
        this.rmSet();
    }

    stopMediaSession() {
        try {
            this.streamingAnalytics.richMedia.removeAll();
        } catch (e) { console.error(e); }
    }

    sendAction(action) {
        try {
            this.streamingAnalytics.richMedia.send({
                action,
                playerId: '1',
                mediaLabel: this.media.title && this.format(this.media.title)
            });
        } catch (e) { console.error(e); }
    }

    /**
     * Initialize events
     */
    initEvents() {
        this.logger.info(`Initialize ${this.name} Analytics Events`);

        this.playerManager.addEventListener(
            cast.framework.events.EventType.PLAYER_LOADING,
            (event) => {
                this.playerLoadingEvent(event);
            }
        );

        this.playerManager.addEventListener(
            cast.framework.events.EventType.MEDIA_FINISHED,
            (event) => {
                this.mediaFinishedEvent(event);
            }
        );

        this.playerManager.addEventListener(
            cast.framework.events.EventType.PLAYING,
            (event) => {
                this.playingEvent(event);
            }
        );

        this.playerManager.addEventListener(
            cast.framework.events.EventType.PAUSE,
            (event) => {
                this.pauseEvent(event);
            }
        );

        this.playerManager.addEventListener(
            cast.framework.events.EventType.SEEKED,
            (event) => {
                this.mediaSeekEvent(event);
            }
        );

        // Fired when the first break clip in a break starts loading.
        // Event is cast.framework.events.BreaksEvent.
        this.playerManager.addEventListener(
            cast.framework.events.EventType.BREAK_STARTED,
            (event) => {
                console.warn('BREAK_STARTED');
            }
        );

        // Fired when the last break clip in a break ends.
        // Event is cast.framework.events.BreaksEvent.
        // Note: This event fires even if a user skips the last break clip in a break
        // to end the break.
        this.playerManager.addEventListener(
            cast.framework.events.EventType.BREAK_ENDED,
            (event) => {
                console.warn('BREAK_ENDED');
            }
        );

        // Fired when a break clip starts loading. Event is cast.framework.events.BreaksEvent.
        this.playerManager.addEventListener(
            cast.framework.events.EventType.BREAK_CLIP_LOADING,
            (event) => {
                console.warn('BREAK_CLIP_LOADING');
            }
        );

        // Fired when a break clip starts. Event is cast.framework.events.BreaksEvent.
        this.playerManager.addEventListener(
            cast.framework.events.EventType.BREAK_CLIP_STARTED,
            (event) => {
                console.warn('BREAK_CLIP_STARTED');
            }
        );

        // Fired when a break clip ends. Event is cast.framework.events.BreaksEvent.
        // Note: This event fires even if a user skips a break clip.
        // Please check the endedReason property of BreaksEvent. To figure out amount of time
        // the break clip was watched please check getBreakClipCurrentTimeSec
        // and getBreakClipDurationSec of PlayerManager.
        this.playerManager.addEventListener(
            cast.framework.events.EventType.BREAK_CLIP_ENDED,
            (event) => {
                console.warn('BREAK_CLIP_ENDED');
            }
        );
    }

    playerLoadingEvent(event) {
        this.logger.info(`${this.name}: Player loading Event`);
        this.startMediaSession(event);
    }

    mediaFinishedEvent(event) {
        // notifyEnd
        this.logger.info(`${this.name}: Media Finished Event`);
        this.logger.debug('event', event);
        this.sendAction('stop');
    }

    playingEvent(event) {
        this.logger.info(`${this.name}: Playing Event`);
        this.logger.debug('event', event);
        this.sendAction('play');
    }

    pauseEvent(event) {
        this.logger.info(`${this.name}: Pause Event`);
        this.logger.debug('event', event);
        this.sendAction('pause');
    }

    mediaSeekEvent(event) {
        this.logger.info(`${this.name}: Media Seek Event`);
        this.logger.debug('event', event);
        this.sendAction('move');
    }

    parseCustomData() {
        const mInfo = this.mediaInformation || {};
        const cData = (this.customData && this.customData.pauseOverlay) || {};
        const { media } = this;

        media.contentId = mInfo.contentId || '';
        media.title = cData.title || '';
        media.duration = cData.duration || 0;
        media.pubDate = cData.pubDate || '';
        media.channel = cData.channel || '';
        media.genre = cData.genre || '';
        media.playlistProgName = cData.playlistProgName || '';
        media.episodeName = cData.episodeName || '';
        media.episodeNumber = cData.episodeNumber || '';
        media.seriesNumber = cData.seriesNumber || '';
        media.subGenre = cData.subGenre || '';
        media.pubDate = cData.pubDate || '';
        media.producer = cData.producer || '';
        media.broadcastMode = (mInfo.streamType === cast.framework.messages.StreamType.LIVE)
            ? this.broadcastTypes.LIVE : this.broadcastTypes.VOD;
    }

    rmSet() {
        const mTypes = this.mediaTypes;
        const pTypes = this.pageTypes;
        const media = this.media || {};

        const options = {
            mediaType: mTypes.VIDEO,
            mediaLabel: media.title && this.format(media.title),
            mediaLevel2: pTypes.LEVEL2,
            mediaTheme1: media.playlistProgName && this.format(media.playlistProgName), // Playlist name
            mediaTheme2: media.pubDate && this.format(media.pubDate), // Publication date
            mediaTheme3: media.producer && this.format(media.producer), // Content provider
            content: media.contentId && this.format(media.contentId),
            playerId: '1',
            refreshDuration: 5,
            duration: media.duration,
            isEmbedded: false,
            broadcastMode: media.broadcastMode
        };

        try {
            this.streamingAnalytics.richMedia.add(options);
        } catch (e) { console.error(e); }
    }

    pageSet() {
        try {
            this.streamingAnalytics.page.set({
                name: this.pageTypes.NAME,
                level2: this.pageTypes.LEVEL2
            });
        } catch (e) { console.error(e); }

        const tConst = this.trackConstants;
        const pTypes = this.pageTypes;
        const media = this.media || {};

        const options = {
            site: {
                1: tConst.MEDIA_TYPE, // Media_Type
                2: tConst.PLATFORM, // Platform
                3: tConst.APP_NAME, // App_Name
                4: media.contentId && this.format(media.contentId), // Content_ID
                5: media.contentId && this.format(media.contentId), // BRN_ID
                6: this.format(tConst.APP_NAME), // Media_Player
                7: this.format(tConst.PLAYER_VERSION), // Player_Version
                8: pTypes.LEVEL2, // Level2
                9: this.format(pTypes.NAME), // Chapter1
                10: media.channel && this.format(media.channel), // Channel
                11: media.genre && this.format(media.genre), // Genre
                12: media.playlistProgName && this.format(media.playlistProgName), // Playlist_Prog_Name
                13: media.episodeName && this.format(media.episodeName), // Episode_Name
                14: '', // Parent_URL
                15: media.episodeNumber, // episode_Number
                16: media.seriesNumber, // Series_Number
                17: 0, // Radio
                18: media.subGenre && this.format(media.subGenre), // Sub_Genre
                19: '', // content_ID
                20: '' // Boxset
            }
        };

        try {
            this.streamingAnalytics.customVars.set(options);
            this.streamingAnalytics.dispatch();
        } catch (e) { console.error(e); }
    }

    format(string) {
        if (!string) {
            return false;
        }

        /*
         * According to ATInternet documentation,
         * all site/page vars strings must be between brackets.
         */
        return `[${this.constructor.formatString(string)}]`;
    }

    static formatString(string) {
        return string && string.replace(/\s/g, '_');
    }
}
